
<template>
    <div class="interventions-page container mt-5">
      <h1 class="Title">Interventions</h1>
      <!-- LISTE -->
      <div v-if="showList">
        <!-- Filtres avancés -->
        <div class="filters d-flex flex-wrap align-items-end gap-2 mb-3">
          <!-- Search -->
          <b-form-group label="Recherche" class="mb-0">
            <b-form-input v-model="search" placeholder="Recherche..."/>
          </b-form-group>

          <!-- Filtre utilisateur -->
          <b-form-group label="Utilisateur" class="mb-0">
            <b-form-select v-model="selectedUser" :options="userOptions">
              <template #first>
                <option :value="null">Tous</option>
              </template>
            </b-form-select>
          </b-form-group>

          <!-- Statut + Trier par dans le même bloc -->
          <div class="d-flex flex-row flex-wrap align-items-end gap-2 mb-0" style="min-width:200px;">
            <b-form-group label="Statut" class="mb-0 me-2">
            <b-form-select v-model="selectedStatut" :options="statutOptions">
              <template #first>
                <option :value="null">Tous</option>
              </template>
            </b-form-select>
          </b-form-group>
          <b-form-group label="Trier par" class="mb-0">
            <b-form-select v-model="sortBy">
              <option value="date_desc">Date ↓</option>
              <option value="date_asc">Date ↑</option>
              <option value="alpha_asc">Titre A→Z</option>
              <option value="alpha_desc">Titre Z→A</option>
            </b-form-select>
            </b-form-group>
          </div>
        </div>
        <div v-if="interventions.length">
          <ul class="list-group">
            <li
                v-for="interv in filteredInterventions"
                :key="interv.id"
                class="list-group-item d-flex justify-content-between align-items-center"
            >
              <div>
                <div>
                  <div class="interv-status-row">
                    <span class="text-muted me-2">#{{ interv.id }}</span>
                    <b-badge :variant="getStatutLabel(interv.statut).color" class="me-2 align-middle">
                      {{ getStatutLabel(interv.statut).label }}
                    </b-badge>
                    <span
                      v-if="getMemoTraitePreview(interv)"
                      class="memo-traite-preview"
                      :title="interv.memo_traite"
                    >{{ getMemoTraitePreview(interv) }}</span>
                  </div>
                  <strong>Titre :</strong> {{ interv.title }}
                </div>
                Client : {{ interv.client_name || '— Aucun client —' }}<br/>
                Technicien : {{ interv.user_name }}<br/>
                Date d'intervention : {{ formatDateFR(interv.date_intervention) }}
                <template v-if="interv.statut === 2 && interv.date_traite">
                  <br/>Traité le : {{ formatDateTimeFR(interv.date_traite) }}
                </template>
              </div>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="openDetailModal(interv)"
              >
                Détails
              </button>
            </li>
          </ul>
        </div>
        <div v-else class="alert alert-info">
          Aucune intervention trouvée.
        </div>
      </div>

      <!-- MODAL DÉTAILS -->
      <b-modal
        v-model="isDetailModalVisible"
        @hidden="onDetailModalHidden"
        id="detailModal"
        title="Détails de l'intervention"
        hide-footer
        body-class="modal-detail-content"
      >
        <div v-if="detailIntervention">
          <p class="small">Intervention : #{{ detailIntervention.id }}</p>
          
          <div class="intervention-info-group">
            <div class="info-row">
              <span class="info-label">Titre</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ detailIntervention.title }}</strong></span>
            </div>
            <div v-if="detailIntervention.client_name" class="info-row">
              <span class="info-label">Client</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ detailIntervention.client_name }}</strong></span>
            </div>
            <div class="info-row">
              <span class="info-label">Technicien</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ detailIntervention.user_name }}</strong></span>
            </div>
            <div class="info-row">
              <span class="info-label">Date</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ formatDateFR(detailIntervention.date_intervention) }}</strong></span>
            </div>
            <div v-if="detailIntervention.statut === 2 && detailIntervention.date_traite" class="info-row">
              <span class="info-label">Traité le</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ formatDateTimeFR(detailIntervention.date_traite) }}</strong></span>
            </div>
          </div>
          <div v-if="detailIntervention.heure_arrivee && detailIntervention.heure_depart" class="intervention-info-group">
            <div class="info-row">
              <span class="info-label">H. arrivée</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ detailIntervention.heure_arrivee }}</strong></span>
            </div>
            <div class="info-row">
              <span class="info-label">H. départ</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ detailIntervention.heure_depart }}</strong></span>
            </div>
            <div v-if="detailIntervention.pause_duree_minutes" class="info-row">
              <span class="info-label">Pause</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ detailIntervention.pause_duree_minutes }} min</strong></span>
            </div>
          </div>
          
          <hr v-if="detailIntervention.memo_utilisateur || detailIntervention.memo_client || detailIntervention.utilisateurs_photos?.length || detailIntervention.client_photos?.length || linkedTasks?.length || detailIntervention.accountable || detailIntervention.signature" />
          
          <div v-if="detailIntervention.memo_utilisateur" class="memo-container mb-2">
            <div class="memo-title-overlay">Mémo interne</div>
            <div class="memo-display-truncated" @click="openMemoModal('utilisateur')" :key="`utilisateur-memo-${detailIntervention.id}`">
              <div v-html="getTruncatedMemoUtilisateur()" class="memo-content-truncated"></div>
              <div v-if="isMemoUtilisateurTruncated" class="memo-overlay-truncated">
                <span class="click-hint-truncated">Cliquez pour voir le mémo complet</span>
              </div>
            </div>
          </div>
          
          <div v-if="detailIntervention.utilisateurs_photos && detailIntervention.utilisateurs_photos.length" class="mb-2">
            <p><strong>Photos internes :</strong>
              <b-button
                  size="sm"
                  variant="link"
                  @click="openUserPhotos(detailIntervention.utilisateurs_photos)"
              >
                Voir ({{ detailIntervention.utilisateurs_photos.length }})
              </b-button>
            </p>
          </div>
          
          <div v-if="detailIntervention.memo_client" class="memo-container mb-2">
            <div class="memo-title-overlay">Mémo client</div>
            <div class="memo-display-truncated" @click="openMemoModal('client')" :key="`client-memo-${detailIntervention.id}`">
              <div v-html="getTruncatedMemoClient()" class="memo-content-truncated"></div>
              <div v-if="isMemoClientTruncated" class="memo-overlay-truncated">
                <span class="click-hint-truncated">Cliquez pour voir le mémo complet</span>
              </div>
            </div>
          </div>

        <!-- Mémo traité: visible et éditable uniquement si l'intervention est traitée (statut === 2) -->
        <div v-if="detailIntervention.statut === 2" class="mt-3">
          <p><strong>Mémo de traitement :</strong></p>
          <b-form-textarea
            v-model="treatedMemo"
            rows="3"
            max-rows="6"
            placeholder="Ajouter un commentaire sur l'intervention traitée..."
          />
          <div class="text-end mt-2">
            <b-button size="sm" variant="primary" @click="saveTreatedMemo" :disabled="savingTreatedMemo">
              <span v-if="!savingTreatedMemo">Enregistrer le mémo</span>
              <span v-else>Enregistrement...</span>
            </b-button>
          </div>
        </div>
          
          <div v-if="detailIntervention.client_photos && detailIntervention.client_photos.length">
            <p><strong>Photos du client :</strong>
            <b-button
                size="sm"
                variant="link"
                @click="openClientPhotos(detailIntervention.client_photos)"
            >
              Voir ({{ detailIntervention.client_photos.length }})
            </b-button>
            </p>
          </div>
          
          <div v-if="linkedTasks && linkedTasks.length">
            <p><strong>Tâches liées :</strong>
            <b-button
                size="sm"
                variant="link"
                class="p-0 ms-2 align-baseline"
                @click="showLinkedTasksModal = true"
            >
                Voir ({{ linkedTasks.length }})
            </b-button>
            </p>
          </div>

          <div v-if="linkedDocuments && linkedDocuments.length">
            <p><strong>Documents liés :</strong>
            <b-button
                size="sm"
                variant="link"
                class="p-0 ms-2 align-baseline"
                @click="showLinkedDocumentsModal = true"
            >
                Voir ({{ linkedDocuments.length }})
            </b-button>
            </p>
          </div>
          
          <div class="mb-3" v-if="detailIntervention.statut !== 3">
            <b-button
                size="sm"
                variant="info"
                @click="openLinkTaskModal"
                class="me-2"
            >
                Lier une tâche existante
            </b-button>
            <b-button
                size="sm"
                variant="success"
                @click="openLinkDocumentModal"
            >
                Lier un document
            </b-button>
          </div>
          
          <div v-if="detailIntervention.accountable">
            <p><strong>Responsable :</strong> {{ detailIntervention.accountable }}</p>
          </div>
          
          <div v-if="detailIntervention.signature">
            <p><strong>Signature :</strong>
              Signé
              <b-button size="sm" variant="link" @click="openSignature(detailIntervention.signature)">
                Voir
              </b-button>
            </p>
          </div>




          <!-- Bouton Imprimer - visible uniquement sur PC -->
          <div class="text-end mt-2 d-none d-md-block" :key="`print-button-${detailIntervention?.id}`">
            <b-button size="sm" variant="info" @click="printIntervention(detailIntervention)">
              <i class="fas fa-print me-1"></i>Imprimer
            </b-button>
          </div>
        </div>

        <div v-else>
          Chargement…
        </div>
        <div class="modal-action-row d-flex flex-wrap gap-2 mt-3" :key="`action-buttons-${detailIntervention?.id}-${detailIntervention?.statut}`">
          <b-button
              variant="secondary"
              @click="isDetailModalVisible = false"
              class="flex-fill"
          >Fermer</b-button>

          <b-button
              v-if="(detailIntervention?.statut === 0) || (detailIntervention?.statut === 1 && !detailIntervention?.signature)"
              variant="primary"
              @click="goToEdit(detailIntervention.id)"
              class="flex-fill"
          >Modifier</b-button>

          <b-button
              v-if="(detailIntervention?.statut === 0) || (detailIntervention?.statut === 1 && !detailIntervention?.signature)"
              variant="danger"
              @click="cancelIntervention(detailIntervention.id)"
              class="flex-fill btn-small-text"
          >Annuler intervention</b-button>

          <!-- Bouton Traiter - visible uniquement sur PC -->
          <b-button
              v-if="[0,1,3].includes(detailIntervention?.statut)"
              variant="warning"
              @click="archiveIntervention(detailIntervention.id)"
              class="flex-fill d-none d-md-block"
          >Traiter</b-button>
          </div>
      </b-modal>





      <!-- MODAL SIGNATURE -->
      <b-modal
        v-model="showSignatureModal"
        id="signatureModal"
        title="Signature"
        hide-footer
        size="lg"
      >
        <div class="text-center">
          <img :src="signatureImageUrl" alt="Signature" style="max-width: 100%;"/>
        </div>
      </b-modal>

      <!-- MODAL MÉMO COMPLET -->
      <b-modal
        v-model="showMemoModal"
        id="memoModal"
        :title="memoModalTitle"
        hide-footer
        size="lg"
        centered
      >
        <div v-if="memoModalContent" class="memo-full-content">
          <div class="memo-text-full" v-html="memoModalContent"></div>
        </div>
        <div v-else class="text-center text-muted">
          Aucun contenu à afficher
        </div>
      </b-modal>

      <!-- MODAL PHOTOS CLIENT (idem pour utilisateur) -->
      <b-modal
          v-model="showClientPhotosModal"
          id="clientPhotosModal"
          :title="modalClientPhotosTitle"
          hide-footer
          size="lg"
      >
        <!-- S'il y a une seule photo, l'afficher directement en grand -->
        <div v-if="clientPhotosToShow.length === 1" class="text-center">
          <img
              :src="`data:image/png;base64,${clientPhotosToShow[0].image}`"
              :alt="clientPhotosToShow[0].nom"
              style="max-width:90vw; max-height:70vh; border-radius:8px;"
          />
        </div>
        <!-- Sinon, afficher la liste des miniatures cliquables -->
        <div v-else-if="clientPhotosToShow.length > 1" class="d-flex flex-wrap justify-content-center">
          <img
              v-for="(photo, idx) in clientPhotosToShow"
              :key="idx"
              :src="`data:image/png;base64,${photo.image}`"
              :alt="photo.nom"
              class="img-thumbnail me-2 mb-2"
              style="max-width:150px; max-height:150px; width:auto; height:auto; cursor:pointer; object-fit:contain;"
              @click="openPhotoLarge(photo)"
              title="Cliquez pour voir en grand"
          />
        </div>
        <div v-else>
          Aucune photo associée.
        </div>
      </b-modal>

      <!-- MODAL PHOTOS UTILISATEUR -->
      <b-modal
          v-model="showUserPhotosModal"
          id="userPhotosModal"
          title="Photos utilisateur"
          hide-footer
          size="lg"
      >
        <!-- S'il y a une seule photo -->
        <div v-if="userPhotosToShow.length === 1" class="text-center">
          <img
              :src="`data:image/png;base64,${userPhotosToShow[0].image}`"
              :alt="userPhotosToShow[0].nom"
              style="max-width:90vw; max-height:70vh; border-radius:8px;"
          />
          <div class="mt-2">{{ userPhotosToShow[0].nom }}</div>
        </div>
        <!-- Plusieurs photos : miniatures cliquables -->
        <div v-else-if="userPhotosToShow.length > 1" class="d-flex flex-wrap justify-content-center">
          <img
              v-for="(photo, idx) in userPhotosToShow"
              :key="idx"
              :src="`data:image/png;base64,${photo.image}`"
              :alt="photo.nom"
              class="img-thumbnail me-2 mb-2"
              style="max-width:150px; max-height:150px; width:auto; height:auto; cursor:pointer; object-fit:contain;"
              @click="openPhotoLarge(photo)"
              title="Cliquez pour voir en grand"
          />
        </div>
        <!-- Aucune photo -->
        <div v-else>
          Aucune photo associée.
        </div>
      </b-modal>

      <!-- MODAL APERÇU PHOTO EN GRAND -->
      <b-modal
          v-model="showPhotoLargeModal"
          id="photoLargeModal"
          :title="photoLargeToShow?.nom || ''"
          hide-footer
          size="xl"
      >
        <div class="photo-container" :class="{ 'zoomed': isZoomed }">
          <img
              v-if="photoLargeToShow"
              :src="`data:image/png;base64,${photoLargeToShow.image}`"
              :alt="photoLargeToShow.nom"
              class="photo-image"
              :class="{ 'zoomed': isZoomed, 'dragging': isDragging }"
              :style="{ transform: isZoomed ? `scale(2) translate(${imagePosition.x}px, ${imagePosition.y}px)` : 'scale(1)' }"
              @dblclick="toggleZoom"
              @mousedown="startDrag"
              @mousemove="drag"
              @mouseup="endDrag"
              @mouseleave="endDrag"
          />
          <div v-else>Aucune photo.</div>
        </div>
        <div class="text-center mt-2">
          <small class="text-muted">Double-cliquez pour zoomer/dézoomer • Glissez-déposez quand zoomé</small>
        </div>
      </b-modal>

      <!-- MODAL NOUVELLE INTERVENTION -->
      <b-modal
          v-model="showNewInterventionModal"
          title="Créer une nouvelle intervention"
          hide-footer
          no-focus
          scrollable
          @hidden="resetNewInterventionForm"
      >
        <b-form @submit.prevent="createNewIntervention">

          <b-form-group label="Rechercher un client :" class="client-filter-container">
            <div class="client-search-field">
              <input
                v-model="clientSearch"
                type="text"
                class="form-control client-search-input"
                placeholder="Tapez pour rechercher un client..."
                autocomplete="off"
                autocapitalize="none"
                autocorrect="off"
                spellcheck="false"
                inputmode="search"
                enterkeyhint="search"
                @input="filterClients"
                @keydown.enter.prevent="selectSingleClientOnEnter"
                @focus="onClientInputFocus"
                @blur="onClientInputBlur"
              />
              <div v-if="showClientResults && filteredClientOptions.length > 0" class="client-search-results mt-2">
                <div
                  v-for="client in filteredClientOptions"
                  :key="client.value"
                  class="client-option p-2 border-bottom cursor-pointer"
                  @mousedown.prevent
                  @click="selectClient(client)"
                  :class="{ 'bg-light': selectedClientForCreation === client.value }"
                >
                  {{ client.text }}
                </div>
              </div>
              <div v-else-if="showClientResults && clientSearch && filteredClientOptions.length === 0" class="text-muted mt-2">
                Aucun client trouvé
              </div>
            </div>
            <div v-if="selectedClientForCreation && selectedClientForCreation !== 0" class="mt-2">
              <div class="selected-client-display">
                <i class="fas fa-check me-2"></i>
                <strong>{{ getSelectedClientName() }}</strong>
              </div>
            </div>
          </b-form-group>

          <b-form-group label="Titre de l'intervention :">
            <b-form-input v-model="newInterventionTitle" placeholder="Entrez le titre..." />
          </b-form-group>

          <p class="text-muted small mb-2">Renseignez au moins un client ou un titre.</p>
          <div v-if="newInterventionError" class="text-danger small mb-2">{{ newInterventionError }}</div>

          <div class="text-end">
            <b-button variant="secondary" @click="showNewInterventionModal = false">Annuler</b-button>
            <b-button type="submit" variant="success" :disabled="!hasClientOrTitle">Créer</b-button>
          </div>
        </b-form>
      </b-modal>

      <!-- Modal tâches liées -->
      <b-modal v-model="showLinkedTasksModal" title="Tâches liées à cette intervention" hide-footer>
          <div v-if="linkedTasks.length > 0">
            <ul class="list-group">
              <li v-for="task in linkedTasks" :key="task.id" class="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <strong>#{{ task.id }} - {{ task.title }}</strong><br>
                  <small class="text-muted">
                    <div>Client: {{ task.client_name || '— Aucun —' }}</div>
                    <div>Statut: {{ getTaskStatutLabel(task.statut).label }}</div>
                  </small>
                </div>
                <div class="d-flex gap-2">
                  <b-button size="sm" variant="primary" @click="showTaskDetails(task)">
                    Voir
                  </b-button>
                  <b-button size="sm" variant="danger" @click="unlinkTask(task.id)">
                    Délier
                  </b-button>
                </div>
              </li>
            </ul>
          </div>
          <div v-else class="text-center text-muted">
            Aucune tâche liée à cette intervention.
          </div>
      </b-modal>

      <!-- Modal lier tâche existante -->
      <b-modal v-model="showLinkTaskModal" title="Lier une tâche existante" hide-footer>
          <b-form-group label="Sélectionner une tâche :">
            <b-form-select v-model="selectedTaskToLink" :options="availableTasks" value-field="id" text-field="title">
              <template #first>
                <option :value="null">Choisir une tâche...</option>
              </template>
            </b-form-select>
          </b-form-group>
          <div class="text-end mt-3">
            <b-button variant="secondary" @click="showLinkTaskModal = false">Annuler</b-button>
            <b-button variant="primary" @click="linkTask" :disabled="!selectedTaskToLink">
              Lier
            </b-button>
          </div>
      </b-modal>

      <!-- Modal documents liés -->
      <b-modal v-model="showLinkedDocumentsModal" title="Documents liés à cette intervention" hide-footer>
          <div v-if="linkedDocuments.length > 0">
            <ul class="list-group">
              <li v-for="document in linkedDocuments" :key="document.id" class="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ document.nom }}</strong><br>
                  <small class="text-muted">
                    <div>Type: {{ document.type_mime }}</div>
                    <div>Taille: {{ formatFileSize(document.taille) }}</div>
                    <div>Créé le: {{ formatDateFR(document.date_creation) }}</div>
                  </small>
                </div>
                <div class="d-flex gap-2">
                  <b-button size="sm" variant="primary" @click="downloadDocument(document)">
                    Télécharger
                  </b-button>
                  <b-button size="sm" variant="danger" @click="unlinkDocument(document.id)">
                    Délier
                  </b-button>
                </div>
              </li>
            </ul>
          </div>
          <div v-else class="text-center text-muted">
            Aucun document lié à cette intervention.
          </div>
      </b-modal>

      <!-- Modal lier document existant -->
      <b-modal v-model="showLinkDocumentModal" title="Lier un document existant" hide-footer>
          <b-form-group label="Sélectionner un document :">
            <b-form-select v-model="selectedDocumentToLink" :options="availableDocuments" value-field="id" text-field="nom">
              <template #first>
                <option :value="null">Choisir un document...</option>
              </template>
            </b-form-select>
          </b-form-group>
          <div class="text-end mt-3">
            <b-button variant="secondary" @click="showLinkDocumentModal = false">Annuler</b-button>
            <b-button variant="primary" @click="linkDocument" :disabled="!selectedDocumentToLink">
              Lier
            </b-button>
          </div>
      </b-modal>

      <!-- Modal détail tâche -->
      <b-modal v-model="showTaskDetailModal" title="Détail de la tâche" hide-footer>
          <div v-if="selectedTask">
            <p><strong>ID :</strong> {{ selectedTask.id }}</p>
            <p><strong>Titre :</strong> {{ selectedTask.title }}</p>
            <p><strong>Client :</strong> {{ selectedTask.client_name || '— Aucun —' }}</p>
            <p><strong>Technitien :</strong> {{ selectedTask.user_name || '—' }}</p>
            <p><strong>Statut :</strong>
              <b-badge :variant="getTaskStatutLabel(selectedTask.statut).color">
                {{ getTaskStatutLabel(selectedTask.statut).label }}
              </b-badge>
            </p>
            <p><strong>Mémo :</strong></p>
            <div v-if="selectedTask.memo" v-html="selectedTask.memo" class="task-memo-content"></div>
            <p v-else class="text-muted">— Aucun mémo —</p>
          </div>
      </b-modal>

      <!-- Pied de page -->
      <FooterActions
          :buttons="[
      { text: 'Nouvelle', onClick: () => { this.showNewInterventionModal = true } },
      {
        text: 'Accueil',
        onClick: () => { goBack() },
        variant: 'secondary',
      }
    ]"
      />
    </div>
  </template>

  <script>
  import api from "@/plugins/api.js";
  import FooterActions from "@/components/FooterActions.vue";

  export default {
    name: "InterventionsPage",
    components: {FooterActions},
    data() {
      return {
        interventions: [],
        isDetailModalVisible: false,
        showSignaturePad: false,
        signatureBlob: null,
        showSignatureModal: false,
        signatureImageUrl: '',
        showList: true,
        showClientPhotosModal: false,
        showUserPhotosModal: false,
        clientPhotosToShow: [],
        userPhotosToShow: [],
        showPhotoLargeModal: false,
        photoLargeToShow: null,
        // Variables pour le système de zoom
        isZoomed: false,
        isDragging: false,
        dragStart: { x: 0, y: 0 },
        imagePosition: { x: 0, y: 0 },
            // Mémo traité
            treatedMemo: '',
            savingTreatedMemo: false,
        showMemoModal: false,
        memoModalTitle: '',
        memoModalContent: '',
        clientOptions: [],
        selectedClientForCreation: 0,
        selectedStatut: null,
        selectedUser: null,
        userOptions: [],
        search: "",
        sortBy: "date_desc",
        showNewInterventionModal: false,
        newInterventionTitle: '',
        newInterventionError: '',
        newInterventionClient: null,
        hasDuration: true,
        // Variables pour la recherche de clients
        clientSearch: '',
        filteredClientOptions: [],
        showClientResults: false,
        statutOptions: [
          { value: 0, text: 'En cours' },
          { value: 1, text: 'Validé' },
          { value: 2, text: 'Archivé' },
          { value: 3, text: 'Annulé' }
        ],

        // Gestion des tâches liées
        linkedTasks: [],
        showLinkedTasksModal: false,
        showLinkTaskModal: false,
        availableTasks: [],
        selectedTaskToLink: null,
        
        // Modal détail tâche
        showTaskDetailModal: false,
        selectedTask: null,

        // Gestion des documents liés
        linkedDocuments: [],
        showLinkedDocumentsModal: false,
        showLinkDocumentModal: false,
        availableDocuments: [],
        selectedDocumentToLink: null,

      };
    },
    computed: {
      hasClientOrTitle() {
        const hasClient = !!this.selectedClientForCreation && this.selectedClientForCreation !== 0;
        const hasTitle = !!(this.newInterventionTitle || '').trim();
        return hasClient || hasTitle;
      },

      isMemoClientTruncated() {
        if (!this.detailIntervention?.memo_client) return false;
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.detailIntervention.memo_client;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        
        return textContent.split('\n').length > 5;
      },



      isMemoUtilisateurTruncated() {
        if (!this.detailIntervention?.memo_utilisateur) return false;
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.detailIntervention.memo_utilisateur;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        
        return textContent.split('\n').length > 5;
      },

      modalClientPhotosTitle() {
        if (this.clientPhotosToShow.length === 1) return this.clientPhotosToShow[0].nom || '';
        if (this.clientPhotosToShow.length > 1) return 'Photos du client';
        return '';
      },


      filteredInterventions() {
        let out = [...this.interventions];

        // Filtre client
        if (this.sortBy === "date_desc") {
          out = out.sort((a, b) => new Date(b.date_intervention) - new Date(a.date_intervention));
        } else if (this.sortBy === "date_asc") {
          out = out.sort((a, b) => new Date(a.date_intervention) - new Date(b.date_intervention));
        }

        // Filtre utilisateur
        if (this.selectedUser !== null) {
          out = out.filter(i => i.utilisateur_id == this.selectedUser);
        }

        // Filtre statut
        if (this.selectedStatut !== null) {
          out = out.filter(i => Number(i.statut) === Number(this.selectedStatut));
        }

        // Filtre recherche sur titre, nom client, mémo de traitement, etc.
        if (this.search.trim()) {
          const q = this.search.trim().toLowerCase();
          out = out.filter(i =>
              (i.title && i.title.toLowerCase().includes(q)) ||
              (i.client_name && i.client_name.toLowerCase().includes(q)) ||
              (i.user_name && i.user_name.toLowerCase().includes(q)) ||
              (i.memo_traite && String(i.memo_traite).toLowerCase().includes(q))
          );
        }

        // Tri
        if (this.sortBy === "date_desc") {
          out = out.sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation));
        } else if (this.sortBy === "date_asc") {
          out = out.sort((a, b) => new Date(a.date_creation) - new Date(b.date_creation));
        } else if (this.sortBy === "alpha_asc") {
          out = out.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        } else if (this.sortBy === "alpha_desc") {
          out = out.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
        }

        return out;
      },



    },
    async mounted() {
      this.fetchInterventions();
      await Promise.all([
        this.fetchClients(),
        this.fetchUsers()
      ]);
      // Charger les filtres après avoir récupéré les clients et utilisateurs
      this.loadFiltersFromStorage();
    },
    watch: {
      // Sauvegarder les filtres dans localStorage quand ils changent
      selectedUser() {
        this.saveFiltersToStorage();
      },
      selectedStatut() {
        this.saveFiltersToStorage();
      },
      search() {
        this.saveFiltersToStorage();
      },
      sortBy() {
        this.saveFiltersToStorage();
      },
    },
    methods: {

      // Fonction pour nettoyer le HTML du mémo
      cleanMemoHtml(html) {
        if (!html) return '';
        return html
          .replace(/<p>/g, '')
          .replace(/<\/p>/g, '<br>')
          .replace(/<br><br>/g, '<br>') // Éviter les doubles sauts de ligne
          .replace(/<br>$/g, ''); // Supprimer le dernier <br>
      },

      // Mémo client tronqué
      getTruncatedMemoClient() {
        if (!this.detailIntervention?.memo_client) return '';
        
        // Nettoyer le HTML en remplaçant les <p> par des <br> pour éviter les espaces
        let cleanHtml = this.cleanMemoHtml(this.detailIntervention.memo_client);
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cleanHtml;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        
        const lines = textContent.split('\n');
        if (lines.length <= 5) return cleanHtml;
        
        const truncatedDiv = document.createElement('div');
        truncatedDiv.innerHTML = cleanHtml;
        
        const allElements = Array.from(truncatedDiv.children);
        let lineCount = 0;
        let elementsToRemove = [];
        
        for (let element of allElements) {
          const elementText = element.textContent || element.innerText || '';
          const elementLines = elementText.split('\n');
          
          if (lineCount + elementLines.length > 5) {
            elementsToRemove.push(element);
            break;
          }
          lineCount += elementLines.length;
        }
        
        elementsToRemove.forEach(el => el.remove());
        
        const ellipsis = document.createElement('span');
        ellipsis.textContent = '...';
        ellipsis.style.fontWeight = 'bold';
        truncatedDiv.appendChild(ellipsis);
        
        return truncatedDiv.innerHTML;
      },

      // Mémo utilisateur tronqué
      getTruncatedMemoUtilisateur() {
        if (!this.detailIntervention?.memo_utilisateur) return '';
        
        // Nettoyer le HTML en remplaçant les <p> par des <br> pour éviter les espaces
        let cleanHtml = this.cleanMemoHtml(this.detailIntervention.memo_utilisateur);
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cleanHtml;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        
        const lines = textContent.split('\n');
        if (lines.length <= 5) return cleanHtml;
        
        const truncatedDiv = document.createElement('div');
        truncatedDiv.innerHTML = cleanHtml;
        
        const allElements = Array.from(truncatedDiv.children);
        let lineCount = 0;
        let elementsToRemove = [];
        
        for (let element of allElements) {
          const elementText = element.textContent || element.innerText || '';
          const elementLines = elementText.split('\n');
          
          if (lineCount + elementLines.length > 5) {
            elementsToRemove.push(element);
            break;
          }
          lineCount += elementLines.length;
        }
        
        elementsToRemove.forEach(el => el.remove());
        
        const ellipsis = document.createElement('span');
        ellipsis.textContent = '...';
        ellipsis.style.fontWeight = 'bold';
        truncatedDiv.appendChild(ellipsis);
        
        return truncatedDiv.innerHTML;
      },

      // Récupère la liste des interventions depuis l'API (sans les photos pour optimiser les performances)
      async fetchInterventions() {
        try {
          const { data } = await api.get(
            "/interventions/",
            { 
              headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
              params: { include_photos: false } // Paramètre pour ne pas charger les photos
            }
          );
          this.interventions = data;
        } catch (err) {
          console.error("Impossible de charger les interventions :", err);
        }
      },

      // Charge les détails complets d'une intervention (avec photos)
      async fetchInterventionDetails(interventionId) {
        try {
          const { data } = await api.get(
            `/interventions/${interventionId}/`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }}
          );
          return data;
        } catch (err) {
          console.error("Impossible de charger les détails de l'intervention :", err);
          return null;
        }
      },

      // Crée une nouvelle intervention
      async createNewIntervention() {
        this.newInterventionError = '';
        if (!this.hasClientOrTitle) {
          this.newInterventionError = 'Renseignez au moins un client ou un titre.';
          return;
        }

        try {
          // Récupère l'utilisateur courant depuis le localStorage
          const rawUser = localStorage.getItem('user');
          const user = rawUser ? JSON.parse(rawUser) : null;
          const utilisateurId = user?.id || null;

          const hasClient = !!this.selectedClientForCreation && this.selectedClientForCreation !== 0;
          const title = (this.newInterventionTitle || '').trim();

          // Création de l'intervention via l'API avec les champs obligatoires
          const payload = {
            utilisateur: utilisateurId,
            client: hasClient ? this.selectedClientForCreation : null,
            title: title || 'Nouvelle intervention',
            date_intervention: new Date().toISOString().substring(0,10),
            // Toujours créer des interventions sans durée
            heure_arrivee: null,
            heure_depart: null
          };

          const { data } = await api.post("/interventions/", payload);

          this.showNewInterventionModal = false;

          // Redirige immédiatement sur la page d'édition
          this.$router.push({ name: 'intervention-edit', params: { id: data.id } });

        } catch (err) {
          console.error('Erreur création intervention:', err);
          alert('Impossible de créer l\'intervention');
        }
      },

      resetNewInterventionForm() {
        this.newInterventionTitle = '';
        this.newInterventionError = '';
        this.selectedClientForCreation = 0;
        this.clientSearch = '';
        this.showClientResults = false;
        // Initialiser avec tous les clients (limités à 20)
        this.filteredClientOptions = this.clientOptions.slice(0, 20);
      },

      // Méthodes pour la recherche de clients
      filterClients() {
        const searchTerm = (this.clientSearch || '').trim();
        if (!searchTerm) {
          this.filteredClientOptions = [];
          this.showClientResults = false;
          return;
        }

        this.showClientResults = true;
        this.filteredClientOptions = this.clientOptions.filter(client =>
          client.text.toLowerCase().includes(searchTerm.toLowerCase())
        ).slice(0, 10);
      },

      selectSingleClientOnEnter() {
        // En cas de résultat unique, Enter sélectionne automatiquement le client.
        if (this.filteredClientOptions.length === 1) {
          this.selectClient(this.filteredClientOptions[0]);
        }
      },

      selectClient(client) {
        this.selectedClientForCreation = client.value;
        this.clientSearch = client.text;
        this.filteredClientOptions = [];
        this.showClientResults = false;
      },

      getSelectedClientName() {
        const client = this.clientOptions.find(c => c.value === this.selectedClientForCreation);
        return client ? client.text : '';
      },

      // Méthodes pour le focus/blur
      onClientInputFocus() {
        this.filterClients();
      },

      onClientInputBlur() {
        // Délai plus long sur mobile pour laisser le tap sur un résultat aboutir
        setTimeout(() => {
          this.showClientResults = false;
        }, 400);
      },

      // Récupère la liste des utilisateurs pour le filtre
      async fetchUsers() {
        try {
          const { data } = await api.get("/utilisateurs/");
          this.userOptions = data.map(u => ({ value: u.id, text: u.name }));
        } catch (err) {
          this.userOptions = [];
        }
      },

      // Charge les filtres depuis localStorage ou utilise les valeurs par défaut
      loadFiltersFromStorage() {
        try {
          const savedFilters = localStorage.getItem('interventions_filters');
          
          if (savedFilters) {
            const filters = JSON.parse(savedFilters);
            // Restaurer les filtres sauvegardés
            this.selectedUser = filters.selectedUser !== undefined ? filters.selectedUser : this.getDefaultUserId();
            this.selectedStatut = filters.selectedStatut !== undefined ? filters.selectedStatut : 0; // En cours par défaut
            this.search = filters.search || "";
            this.sortBy = filters.sortBy || "date_desc";
          } else {
            // Utiliser les valeurs par défaut si rien n'est sauvegardé
            this.selectedUser = this.getDefaultUserId();
            this.selectedStatut = 0; // En cours
            this.search = "";
            this.sortBy = "date_desc";
            // Sauvegarder les valeurs par défaut
            this.saveFiltersToStorage();
          }
        } catch (error) {
          console.error('Erreur lors du chargement des filtres:', error);
          // En cas d'erreur, utiliser les valeurs par défaut
          this.selectedUser = this.getDefaultUserId();
          this.selectedStatut = 0;
          this.search = "";
          this.sortBy = "date_desc";
        }
      },

      // Sauvegarde les filtres dans localStorage
      saveFiltersToStorage() {
        try {
          const filters = {
            selectedUser: this.selectedUser,
            selectedStatut: this.selectedStatut,
            search: this.search,
            sortBy: this.sortBy
          };
          localStorage.setItem('interventions_filters', JSON.stringify(filters));
        } catch (error) {
          console.error('Erreur lors de la sauvegarde des filtres:', error);
        }
      },

      // Récupère l'ID de l'utilisateur connecté depuis localStorage
      getDefaultUserId() {
        try {
          const rawUser = localStorage.getItem('user');
          if (rawUser) {
            const user = JSON.parse(rawUser);
            return user?.id || null;
          }
        } catch (error) {
          console.error('Erreur lors de la récupération de l\'utilisateur:', error);
        }
        return null;
      },

      async cancelIntervention(id) {
        if (!confirm(`Annuler l'intervention ?`)) return;
        try {
          await api.patch(`/interventions/${id}/`, { statut: 3 });
          await this.fetchInterventions();
          this.isDetailModalVisible = false;
        } catch(e) {
          console.error("Erreur annulation :", e);
        }
      },

      async archiveIntervention(id) {
        const confirmMessage = "Êtes-vous sûr de vouloir traiter cette intervention ?\n\nUne fois traitée, elle ne pourra plus être modifiée.";
        if (!confirm(confirmMessage)) return;
        
        try {
          await api.patch(`/interventions/${id}/`, { statut: 2 });
          await this.fetchInterventions();
          this.isDetailModalVisible = false;
          alert("Intervention traitée avec succès.");
        } catch(e) {
          console.error("Erreur traitement :", e);
          alert("Erreur lors du traitement de l'intervention.");
        }
      },

      // Ouvrir la modal pour afficher le mémo complet
      openMemoModal(type) {
        if (type === 'client' && this.detailIntervention?.memo_client) {
          this.memoModalTitle = 'Mémo client complet';
          this.memoModalContent = this.cleanMemoHtml(this.detailIntervention.memo_client);
          this.showMemoModal = true;
        } else if (type === 'utilisateur' && this.detailIntervention?.memo_utilisateur) {
          this.memoModalTitle = 'Mémo intene complet';
          this.memoModalContent = this.cleanMemoHtml(this.detailIntervention.memo_utilisateur);
          this.showMemoModal = true;
        }
      },

      async fetchClients() {
        try {
          const { data } = await api.get("/interventions/contacts");
          this.clientOptions = data.map(c => (
              {
            value: c.codechrono,
            text: c.nom_affichage
          }));
          this.filteredClientOptions = this.clientOptions.slice(0, 20);
        } catch (err) {
          this.clientOptions = [];
          this.filteredClientOptions = [];
        }
      },

      async printIntervention(intervention) {
        // Utiliser les détails déjà chargés si disponibles, sinon les charger
        let fullIntervention = this.detailIntervention;
        if (!fullIntervention || !fullIntervention.client_photos || !fullIntervention.utilisateurs_photos) {
        const { data } = await api.get(`/interventions/${intervention.id}/`);
          fullIntervention = data;
        }

        function extractBase64(image) {
          if (!image) return '';
          if (typeof image === 'string') return image;
          if (image?.data) {
            const byteArray = Uint8Array.from(image.data);
            return btoa(String.fromCharCode(...byteArray));
          }
          return '';
        }

        function formatDateFR(dateStr) {
          if (!dateStr) return '';
          const d = new Date(dateStr);
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const year = d.getFullYear();
          return `${day}-${month}-${year}`;
        }

        const win = window.open('', '_blank');
        win.document.write(`
    <html>
      <head>
        <title>Intervention</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          h2 { border-bottom: 2px solid #000; padding-bottom: 5px; }
          img { margin: 10px 0; }
          .page-break { page-break-before: always; }
        </style>
      </head>
      <body>
  `);

        // --- Page 1 : Informations ---
        win.document.write(`<h2>Intervention #${fullIntervention.id}</h2>`);
        win.document.write(`<p><strong>Titre:</strong> ${fullIntervention.title}</p>`);
        win.document.write(`<p><strong>Client:</strong> ${fullIntervention.client_name || 'Aucun client'}</p>`);
        win.document.write(`<p><strong>Technicien:</strong> ${fullIntervention.user_name}</p>`);
        win.document.write(`<p><strong>Date d'intervention:</strong> ${formatDateFR(fullIntervention.date_intervention)}</p>`);
        win.document.write(`<p><strong>Nombre d'intervenants:</strong> ${fullIntervention.nb_intervenants || 1}</p>`);
        if (fullIntervention.heure_arrivee && fullIntervention.heure_depart) {
        win.document.write(`<p><strong>Heure d'arrivée:</strong> ${fullIntervention.heure_arrivee}</p>`);
        win.document.write(`<p><strong>Heure de départ:</strong> ${fullIntervention.heure_depart}</p>`);
        
        // Calculer la durée d'intervention (départ - arrivée - pause)
        // Gérer le format avec ou sans secondes (HH:MM:SS ou HH:MM)
        const arrivalTimeStr = fullIntervention.heure_arrivee.includes(':') ? fullIntervention.heure_arrivee : `${fullIntervention.heure_arrivee}:00`;
        const departureTimeStr = fullIntervention.heure_depart.includes(':') ? fullIntervention.heure_depart : `${fullIntervention.heure_depart}:00`;
        
        const arrivalTime = new Date(`2000-01-01T${arrivalTimeStr}`);
        const departureTime = new Date(`2000-01-01T${departureTimeStr}`);
        const pauseMinutes = fullIntervention.pause_duree_minutes || 0;
        
        let totalMinutes = (departureTime - arrivalTime) / (1000 * 60) - pauseMinutes;
        if (totalMinutes < 0) totalMinutes = 0; // Éviter les durées négatives
        
        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.round(totalMinutes % 60);
        const durationText = hours > 0 ? `${hours}h${minutes > 0 ? minutes.toString().padStart(2, '0') : ''}` : `${minutes}min`;
        
        win.document.write(`<p><strong>Durée d'intervention(par personne):</strong> ${durationText}`);
        if (pauseMinutes > 0) {
          win.document.write(` (pause déjà déduite: ${pauseMinutes}min)`);
        }
        win.document.write(`</p>`);
        } else {
          win.document.write(`<p><strong>Durée:</strong> Aucune durée définie pour cette intervention</p>`);
        }
        win.document.write(`<p><strong>Mémo client:</strong><br>${fullIntervention.memo_client || '-'}</p>`);
        win.document.write(`<p><strong>Mémo interne:</strong><br>${fullIntervention.memo_utilisateur || '-'}</p>`);

        win.document.write(`<h3>Signature</h3>`);
        if (fullIntervention.signature) {
          win.document.write(`<img src="data:image/png;base64,${extractBase64(fullIntervention.signature)}" style="max-width:400px; border:1px solid #000;">`);
        } else {
          win.document.write('<p>Non signée</p>');
        }

        // --- Page 2 : Photos ---
        win.document.write('<div class="page-break"></div>');
        win.document.write(`<h2>Photos</h2>`);

        win.document.write('<h3>Photos client</h3>');
        if (fullIntervention.client_photos?.length) {
          fullIntervention.client_photos.forEach(photo => {
            win.document.write(`<p>${photo.nom}</p>`);
            win.document.write(`<img src="data:image/png;base64,${extractBase64(photo.image)}" style="max-width:100%; max-height:400px;">`);
          });
        } else {
          win.document.write('<p>Aucune photo client</p>');
        }

        win.document.write('<h3>Photos internes</h3>');
        if (fullIntervention.utilisateurs_photos?.length) {
          fullIntervention.utilisateurs_photos.forEach(photo => {
            win.document.write(`<p>${photo.nom}</p>`);
            win.document.write(`<img src="data:image/png;base64,${extractBase64(photo.image)}" style="max-width:100%; max-height:400px;">`);
          });
        } else {
          win.document.write('<p>Aucune photo utilisateur</p>');
        }

        win.document.write('</body></html>');
        win.document.close();
        win.focus();
        win.print();
        win.onafterprint = () => {
          win.close();
        };
      },

      async printLinkedDocuments() {
        if (!this.linkedDocuments || this.linkedDocuments.length === 0) {
          alert('Aucun document lié à imprimer.');
          return;
        }

        const win = window.open('', '_blank');
        win.document.write(`
    <html>
      <head>
        <title>Documents liés - Intervention #${this.detailIntervention.id}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .document-content { 
            margin: 0; 
            padding: 0;
          }
          .page-break { page-break-before: always; }
          img { max-width: 100%; height: auto; }
          iframe { width: 100%; height: 100vh; border: none; }
          pre { white-space: pre-wrap; font-family: monospace; }
        </style>
      </head>
      <body>
  `);

        // Charger tous les documents d'abord
        const documentContents = [];
        
        for (let index = 0; index < this.linkedDocuments.length; index++) {
          const doc = this.linkedDocuments[index];
          
          try {
            // Récupérer le contenu du document
            const response = await api.get(`/document-files/${doc.id}/content`, {
              responseType: 'blob'
            });
            
            const blob = new Blob([response.data], { type: doc.type_mime || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            
            let contentHtml = '';
            
            // Préparer le contenu selon le type
            if (doc.type_mime?.startsWith('image/')) {
              // Image
              contentHtml = `<img src="${url}" alt="${doc.nom}">`;
            } else if (doc.type_mime === 'application/pdf') {
              // PDF
              contentHtml = `<iframe src="${url}" type="application/pdf"></iframe>`;
            } else if (doc.type_mime?.startsWith('text/')) {
              // Texte
              const text = await blob.text();
              contentHtml = `<pre style="white-space: pre-wrap; font-family: monospace;">${text}</pre>`;
            } else {
              // Autres types - afficher un lien de téléchargement
              contentHtml = `
                <p><strong>Type de fichier non supporté pour l'affichage direct.</strong></p>
                <p>Type MIME: ${doc.type_mime || 'Non spécifié'}</p>
                <p>Taille: ${this.formatFileSize(doc.taille)}</p>
                <p><em>Utilisez le bouton "Télécharger" pour accéder au fichier.</em></p>
              `;
            }
            
            documentContents.push({
              doc,
              contentHtml,
              url
            });
            
          } catch (error) {
            console.error(`Erreur lors du chargement du document ${doc.nom}:`, error);
            documentContents.push({
              doc,
              contentHtml: `
                <div class="document-content">
                  <p><strong>Erreur lors du chargement du document:</strong></p>
                  <p>${doc.nom}</p>
                  <p><em>Impossible de récupérer le contenu du fichier.</em></p>
                </div>
              `,
              url: null
            });
          }
        }

        // Maintenant écrire tout le contenu dans la fenêtre
        documentContents.forEach((item, index) => {
          const { contentHtml } = item;
          
          if (index > 0) {
            win.document.write('<div class="page-break"></div>');
          }
          
          // Afficher seulement le contenu du document (pas les métadonnées)
          win.document.write('<div class="document-content">');
          win.document.write(contentHtml);
          win.document.write('</div>');
        });

        win.document.write('</body></html>');
        win.document.close();
        
        // Attendre que les images/iframes soient chargées avant d'imprimer
        win.onload = () => {
          // Attendre un peu plus pour s'assurer que tout est chargé
          setTimeout(() => {
            win.focus();
            win.print();
            win.onafterprint = () => {
              // Nettoyer les URLs blob
              documentContents.forEach(item => {
                if (item.url) {
                  URL.revokeObjectURL(item.url);
                }
              });
              win.close();
            };
          }, 1000);
        };
      },

      // Formate une date au format français (DD-MM-YYYY)
      formatDateFR(dateStr) {
        if (!dateStr) return '';
        // Prend en compte le fuseau local (français) avec Date()
        const d = new Date(dateStr);
        // On récupère le jour, mois, année en local
        const day   = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0'); // 0-indexé
        const year  = d.getFullYear();
        return `${day}-${month}-${year}`;
      },

      // Formate une date/heure au format français (DD-MM-YYYY HH:mm)
      formatDateTimeFR(dateStr) {
        if (!dateStr) return '';
        let normalized = String(dateStr).trim();
        // Si le backend envoie un timestamp sans timezone, on l'interprète comme UTC
        // puis on convertit à l'affichage en Europe/Paris.
        const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(normalized);
        if (!hasTimezone) {
          normalized = normalized.replace(' ', 'T');
          normalized = `${normalized}Z`;
        }

        const d = new Date(normalized);
        if (Number.isNaN(d.getTime())) return '';

        // Force l'affichage en heure France pour éviter le décalage serveur.
        const parts = new Intl.DateTimeFormat('fr-FR', {
          timeZone: 'Europe/Paris',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).formatToParts(d);

        const get = (type) => parts.find(p => p.type === type)?.value || '';
        return `${get('day')}-${get('month')}-${get('year')} ${get('hour')}:${get('minute')}`;
      },

      // Formate une date ISO en chaîne lisible
      formatDate(isoStr) {
        return new Date(isoStr).toLocaleString();
      },

      onDetailModalHidden() {
        this.detailIntervention = null;
      },
      async openDetailModal(interv) {
        // Afficher la modal immédiatement avec les données de base
        this.detailIntervention = interv;
        this.isDetailModalVisible = true;
        // Pré-remplir le mémo traité si présent
        this.treatedMemo = interv.memo_traite || '';
        
        // Charger les détails complets (avec photos) en arrière-plan
        try {
          const fullDetails = await this.fetchInterventionDetails(interv.id);
          if (fullDetails) {
            // Mettre à jour l'intervention avec les détails complets
            // Utiliser Object.assign pour maintenir la réactivité
            this.detailIntervention = Object.assign({}, this.detailIntervention, fullDetails);
            this.treatedMemo = fullDetails.memo_traite || '';
            
            // Forcer la mise à jour du DOM après le chargement des détails
            this.$nextTick(() => {
              this.$forceUpdate();
            });
          }
        } catch (error) {
          console.error('Erreur lors du chargement des détails:', error);
        }
        
        // Forcer la mise à jour du DOM pour les méthodes
        this.$nextTick(() => {
          // Forcer la réactivité
          this.$forceUpdate();
        });
        
        // Charger les tâches liées
        await this.loadLinkedTasks();
        // Charger les documents liés
        await this.loadLinkedDocuments();
      },

      async saveTreatedMemo() {
        if (!this.detailIntervention || this.detailIntervention.statut !== 2) return;
        this.savingTreatedMemo = true;
        try {
          await api.patch(`/interventions/${this.detailIntervention.id}/`, { memo_traite: this.treatedMemo });
          const updated = await this.fetchInterventionDetails(this.detailIntervention.id);
          if (updated) {
            this.detailIntervention = Object.assign({}, this.detailIntervention, updated);
            const idx = this.interventions.findIndex(i => i.id === this.detailIntervention.id);
            if (idx !== -1) {
              this.interventions[idx] = {
                ...this.interventions[idx],
                memo_traite: updated.memo_traite ?? this.treatedMemo
              };
            }
          }
        } catch (e) {
          console.error('Erreur lors de la sauvegarde du mémo traité:', e);
          alert('Erreur lors de la sauvegarde du mémo.');
        } finally {
          this.savingTreatedMemo = false;
        }
      },
      openSignature(base64data) {
        // préfixe data:image/png;base64, pour affichage img
        this.signatureImageUrl = `data:image/png;base64,${base64data}`;
        this.showSignatureModal = true;
      },
      goBack() {
        this.$router.push("/Accueil");
      },

      // Ouvrir les photos du client ou de l'utilisateur
      openClientPhotos(photos) {
        this.clientPhotosToShow = photos || [];
        this.showClientPhotosModal = true;
      },
      openUserPhotos(photos) {
        this.userPhotosToShow = photos || [];
        this.showUserPhotosModal = true;
      },

      // Ouvrir une photo en grand
      openPhotoLarge(photo) {
        this.photoLargeToShow = photo;
        this.isZoomed = false; // Réinitialiser le zoom
        this.imagePosition = { x: 0, y: 0 }; // Réinitialiser la position
        this.showPhotoLargeModal = true;
      },

      // Méthodes pour le système de zoom
      toggleZoom() {
        this.isZoomed = !this.isZoomed;
        if (!this.isZoomed) {
          // Réinitialiser la position quand on dézoome
          this.imagePosition = { x: 0, y: 0 };
        } else {
          // Centrer la photo quand on zoome
          this.imagePosition = { x: 0, y: 0 };
        }
      },

      startDrag(event) {
        if (!this.isZoomed) return;
        this.isDragging = true;
        this.dragStart = {
          x: event.clientX - this.imagePosition.x,
          y: event.clientY - this.imagePosition.y
        };
        event.preventDefault();
      },

      drag(event) {
        if (!this.isDragging || !this.isZoomed) return;
        
        let newX = event.clientX - this.dragStart.x;
        let newY = event.clientY - this.dragStart.y;
        
        const container = event.target.parentElement;
        const containerRect = container.getBoundingClientRect();
        const imageRect = event.target.getBoundingClientRect();
        
        // Calculer les limites maximales de translation (unscaled)
        // L'image est zoomée 2x, donc les valeurs de translation doivent être divisées par 2
        const maxTranslateX = Math.max(0, (imageRect.width - containerRect.width) / 4);
        const maxTranslateY = Math.max(0, (imageRect.height - containerRect.height) / 4);
        
        // Contrainte les valeurs de déplacement
        newX = Math.max(-maxTranslateX, Math.min(maxTranslateX, newX));
        newY = Math.max(-maxTranslateY, Math.min(maxTranslateY, newY));

        this.imagePosition = { x: newX, y: newY };
        event.preventDefault();
      },

      endDrag() {
        this.isDragging = false;
      },

        // navigation vers la page d'édition
      goToEdit(id) {
        // Vérifier que l'utilisateur courant est le créateur de l'intervention
        try {
          const rawUser = localStorage.getItem('user');
          const currentUser = rawUser ? JSON.parse(rawUser) : null;
          const currentUserId = currentUser?.id;

          // On récupère l'intervention courante soit depuis la modal, soit dans la liste
          let targetIntervention = this.detailIntervention && this.detailIntervention.id === id
            ? this.detailIntervention
            : (this.interventions || []).find(i => i.id === id);

          const creatorUserId = targetIntervention?.utilisateur_id;
          const creatorUserName = targetIntervention?.user_name || 'Utilisateur inconnu';

          if (creatorUserId && currentUserId && Number(creatorUserId) !== Number(currentUserId)) {
            alert(`Attention: vous n'êtes pas le créateur de cette intervention.\nCréée par: ${creatorUserName} (ID: ${creatorUserId}).`);
          }
        } catch (_) { /* ignore parsing errors, fallback to allow */ }

        this.isDetailModalVisible = false;
        // nom de route à adapter selon votre router (ex: 'intervention-edit')
        this.$router.push({ name: 'intervention-edit', params: { id } });
      },

      getStatutLabel(statut) {
        switch (statut) {
          case 0: return { label: "En cours",    color: "warning" };
          case 1: return { label: "Validé",       color: "success" };
          case 2: return { label: "Traité",      color: "secondary" };
          case 3: return { label: "Annulé",      color: "danger" };
          default: return { label: "Inconnu",    color: "dark" };
        }
      },

      getMemoTraitePreview(interv) {
        return (interv?.memo_traite || '').replace(/\s+/g, ' ').trim();
      },


      // Méthodes pour les tâches liées
      async loadLinkedTasks() {
        if (!this.detailIntervention) return;
        
        try {
          const { data } = await api.get(`/interventions/${this.detailIntervention.id}/tasks`);
          this.linkedTasks = data;
        } catch (error) {
          console.error('Erreur lors du chargement des tâches liées:', error);
          this.linkedTasks = [];
        }
      },

      async loadAvailableTasks() {
        try {
          const { data } = await api.get('/tasks');
          // Filtrer les tâches déjà liées
          const linkedIds = this.linkedTasks.map(t => t.id);
          this.availableTasks = data.filter(task => !linkedIds.includes(task.id));
        } catch (error) {
          console.error('Erreur lors du chargement des tâches disponibles:', error);
          this.availableTasks = [];
        }
      },

      async openLinkTaskModal() {
        this.showLinkTaskModal = true;
        await this.loadAvailableTasks();
      },

      async linkTask() {
        if (!this.selectedTaskToLink || !this.detailIntervention) return;
        
        try {
          await api.post(`/interventions/${this.detailIntervention.id}/tasks/${this.selectedTaskToLink}/link`);
          this.showLinkTaskModal = false;
          this.selectedTaskToLink = null;
          await this.loadLinkedTasks();
          await this.loadAvailableTasks();
        } catch (error) {
          console.error('Erreur lors de la liaison de la tâche:', error);
          alert('Erreur lors de la liaison de la tâche. Veuillez réessayer.');
        }
      },

      async unlinkTask(taskId) {
        if (!this.detailIntervention) return;
        
        if (!confirm('Êtes-vous sûr de vouloir délier cette tâche ?')) return;
        
        try {
          await api.delete(`/interventions/${this.detailIntervention.id}/tasks/${taskId}/unlink`);
          await this.loadLinkedTasks();
          await this.loadAvailableTasks();
        } catch (error) {
          console.error('Erreur lors de la déliaison de la tâche:', error);
          alert('Erreur lors de la déliaison de la tâche. Veuillez réessayer.');
        }
      },

      showTaskDetails(task) {
        this.selectedTask = task;
        this.showTaskDetailModal = true;
      },

      getTaskStatutLabel(statut) {
        switch (statut) {
          case 'en_cours': return { label: 'En cours', color: 'warning' };
          case 'terminee': return { label: 'Terminée', color: 'success' };
          case 'annulee': return { label: 'Annulée', color: 'danger' };
          default: return { label: 'Inconnu', color: 'dark' };
        }
      },

      // Méthodes pour les documents liés
      async loadLinkedDocuments() {
        if (!this.detailIntervention) return;
        
        try {
          const { data } = await api.get(`/interventions/${this.detailIntervention.id}/documents`);
          this.linkedDocuments = data;
        } catch (error) {
          console.error('Erreur lors du chargement des documents liés:', error);
          this.linkedDocuments = [];
        }
      },

      async loadAvailableDocuments() {
        try {
          const { data } = await api.get('/document-files');
          // Filtrer les documents déjà liés
          const linkedIds = this.linkedDocuments.map(d => d.id);
          this.availableDocuments = data.filter(document => !linkedIds.includes(document.id));
        } catch (error) {
          console.error('Erreur lors du chargement des documents disponibles:', error);
          this.availableDocuments = [];
        }
      },

      async openLinkDocumentModal() {
        this.showLinkDocumentModal = true;
        await this.loadAvailableDocuments();
      },

      async linkDocument() {
        if (!this.selectedDocumentToLink || !this.detailIntervention) return;
        
        try {
          await api.post(`/interventions/${this.detailIntervention.id}/documents/${this.selectedDocumentToLink}/link`);
          this.showLinkDocumentModal = false;
          this.selectedDocumentToLink = null;
          await this.loadLinkedDocuments();
          await this.loadAvailableDocuments();
        } catch (error) {
          console.error('Erreur lors de la liaison du document:', error);
          alert('Erreur lors de la liaison du document. Veuillez réessayer.');
        }
      },

      async unlinkDocument(documentId) {
        if (!this.detailIntervention) return;
        
        if (!confirm('Êtes-vous sûr de vouloir délier ce document ?')) return;
        
        try {
          await api.delete(`/interventions/${this.detailIntervention.id}/documents/${documentId}/unlink`);
          await this.loadLinkedDocuments();
          await this.loadAvailableDocuments();
        } catch (error) {
          console.error('Erreur lors de la déliaison du document:', error);
          alert('Erreur lors de la déliaison du document. Veuillez réessayer.');
        }
      },

      async downloadDocument(doc) {
        try {
          // Télécharger le document via l'API
          const response = await api.get(`/document-files/${doc.id}/download`, {
            responseType: 'blob'
          });
          
          // Créer un blob et télécharger
          const blob = new Blob([response.data], { 
            type: doc.type_mime || 'application/octet-stream' 
          });
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = doc.nom || 'document';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Nettoyer l'URL
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Erreur lors du téléchargement du document:', error);
          alert('Erreur lors du téléchargement du document. Veuillez réessayer.');
        }
      },

      formatFileSize(bytes) {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
      },
    },
  };
  </script>

  <style scoped>
  .interventions-page { max-width: 800px; margin: auto; padding-bottom: 100px;}
  .list-group-item     { cursor: pointer; }
  .filters {
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
  }
  .filters .form-group { min-width: 160px; }
  .badge {
    vertical-align: middle;
  }

  .interv-status-row {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    min-width: 0;
    margin-bottom: 0.25rem;
  }

  .memo-traite-preview {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.875rem;
    color: #6c757d;
    text-align: left;
  }
  
  /* Alignement du texte à gauche dans les cartes d'interventions */
  .list-group-item > div:first-child {
    text-align: left;
    flex: 1;
    min-width: 0; /* Permet au texte de se rétrécir */
  }
  
  /* Gestion des textes longs */
  .list-group-item strong {
    display: inline-block;
    min-width: 80px; /* Largeur minimale pour les labels */
  }
  
  /* Permettre le retour à la ligne pour les titres longs */
  .list-group-item div > div:first-child {
    word-wrap: break-word;
    word-break: break-word;
    max-width: 100%;
  }
  
  /* Espacement entre les éléments */
  .list-group-item div > div:first-child br {
    margin-bottom: 0.25rem;
  }
  
  /* Styles pour les boutons du modal en mode mobile */
  .modal-action-row {
    justify-content: flex-start;
  }
  
  .modal-action-row .btn {
    min-width: 80px;
    margin-bottom: 0.5rem;
  }
  
  /* Sur mobile, les boutons restent horizontaux mais plus petits */
  @media (max-width: 576px) {
    .modal-action-row {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 0.25rem;
    }
    
    .modal-action-row .btn {
      flex: 1;
      min-width: 70px;
      font-size: 0.875rem;
      padding: 0.375rem 0.5rem;
    }
  }
  
  /* Styles pour l'affichage des mémo avec HTML */
  .memo-display {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    margin: 0.5rem 0;
    line-height: 1.6;
    color: #495057;
    font-family: inherit;
    font-size: 1rem;
  }
  
  .memo-display p {
    margin-bottom: 0.5rem;
  }
  
  .memo-display p:last-child {
    margin-bottom: 0;
  }
  
  .memo-display ul, .memo-display ol {
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
  }
  
  .memo-display strong, .memo-display b {
    font-weight: 600;
  }
  
  .memo-display em, .memo-display i {
    font-style: italic;
  }
  
  /* Styles pour les mémo tronqués */
  .memo-display-truncated {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    margin: 0.5rem 0;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    height: 120px;
    overflow: hidden;
  }
  
  .memo-display-truncated:hover {
    border-color: #2768b1;
    box-shadow: 0 4px 8px rgba(39, 104, 177, 0.2);
  }
  
  .memo-content-truncated {
    line-height: 1.6;
    color: #495057;
    font-family: inherit;
    font-size: 1rem;
    margin: 0;
    padding: 0;
    height: 100%;
    overflow: hidden;
  }
  
  .memo-overlay-truncated {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(248, 249, 250, 0.9));
    padding: 1rem;
    text-align: center;
    pointer-events: none;
  }
  
  .click-hint-truncated {
    background-color: #2768b1;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
  }
  
  /* Styles pour la modal du mémo complet */
  .memo-full-content {
    max-height: 60vh;
    overflow-y: auto;
  }
  
  .memo-text-full {
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.6;
    color: #495057;
    font-family: inherit;
    font-size: 1rem;
    margin: 0;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #dee2e6;
  }
  
  /* Styles pour le contenu du mémo des tâches */
  .task-memo-content {
    margin-top: 0.5rem;
    max-height: 200px;
    overflow-y: auto;
  }
  
  .task-memo-content p {
    margin-bottom: 0.5rem;
  }
  
  .task-memo-content p:last-child {
    margin-bottom: 0;
  }

  /* Styles pour la recherche de clients */
  .client-search-field {
    position: relative;
  }

  .client-search-input {
    font-size: 16px;
    min-height: 44px;
  }

  .client-search-results {
    max-height: 200px;
    max-width: 300px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    margin-top: 2px;
    text-align: left;
  }

  @media (max-width: 768px) {
    .client-search-results {
      position: static;
      max-width: 100%;
      max-height: 160px;
    }
  }

  .client-option {
    transition: background-color 0.2s ease;
  }

  .client-option:hover {
    background-color: #f8f9fa !important;
  }

  .client-option.cursor-pointer {
    cursor: pointer;
  }

  /* Style pour l'affichage du client sélectionné */
  .selected-client-display {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    border-radius: 6px;
    padding: 12px 16px;
    color: #155724;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    max-width: 250px;
    margin: 0 auto;
  }

  .selected-client-display i {
    color: #28a745;
    font-size: 1.2rem;
  }

  /* Conteneur pour le positionnement absolu */
  .client-filter-container {
    position: relative;
  }

  /* Bouton avec texte plus petit */
  .btn-small-text {
    font-size: 0.85rem;
  }

  /* Styles pour le groupe d'informations d'intervention */
  .intervention-info-group {
    margin: 1rem 0;
  }
  
  .info-row {
    display: flex;
    align-items: center;
    margin-bottom: 0.1rem;
  }
  
  .info-label {
    min-width: 80px;
    text-align: left;
  }
  
  .info-separator {
    margin: 0 0.5rem;
    text-align: center;
    min-width: 10px;
  }
  
  .info-value {
    flex: 1;
    text-align: left;
  }

  /* Styles pour les mémos avec titre sur la bordure */
  .memo-container {
    position: relative;
    margin: 1rem 0;
  }

  .memo-title-overlay {
    position: absolute;
    top: -12px;
    left: 15px;
    background: white;
    padding: 0 8px;
    font-weight: bold;
    font-size: 0.9rem;
    color: #000000;
    z-index: 10;
  }

  /* Padding pour éviter que le contenu soit masqué par le footer */
  .modal-detail-content {
    padding-bottom: 800px !important;
  }

  /* Styles pour le système de zoom */
  .photo-container {
    text-align: center;
    overflow: hidden;
    max-height: 70vh;
    max-width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 70vh;
  }

  .photo-container.zoomed {
    overflow: hidden; /* Garder hidden pour les limites */
    max-height: 80vh;
    height: 80vh;
  }

  .photo-image {
    max-width: 90vw;
    max-height: 70vh;
    width: auto;
    height: auto;
    cursor: pointer;
    transition: transform 0.3s ease;
    object-fit: contain;
    user-select: none;
    border-radius: 8px;
    box-shadow: 0 0 8px #0004;
  }

  .photo-image.zoomed {
    cursor: grab;
    transition: none; /* Pas de transition pendant le drag */
  }

  .photo-image.zoomed.dragging {
    cursor: grabbing;
  }

  .photo-image.zoomed:active {
    cursor: grabbing;
  }
  </style>